import React from "react"
import PropTypes from "prop-types"
import { useQuery, useMutation } from "@apollo/client"
import { useRouteMatch } from "react-router-dom"
import moment from "moment"
import { PUT_CASE } from "../../apollo/mutations"
import { subjectsPropTypes } from "../../utils/propTypes"
import Loading from "../Loading"
import CreateEditCase from "."
import {
  LOCATIONS_QUERY,
  USERS_QUERY,
  CASE_LIST,
  EXPOSED_ASSET_LIST,
  EXPOSED_EMPLOYEE_LIST,
  EXPOSED_LOCATION_LIST,
} from "../../apollo/queries"

const CreateEditCaseEnhanced = (props) => {
  const facilityId = useRouteMatch({
    path: "/facility/:facilityId",
  }).params.facilityId

  // If we're just creating a case, we just want to refresh the case list.
  let mutationRefetchQueries = [
    {
      query: CASE_LIST,
      variables: {
        facilityId: facilityId,
        archived: false,
      },
    },
    {
      query: CASE_LIST,
      variables: {
        facilityId: facilityId,
        archived: true,
      },
    },
  ]

  const variables = {
    facilityId: facilityId,
    caseId: props.internalId,
    startAt:
      props.subjects &&
      moment.min(props.subjects.map((subject) => subject.startAt)).format(),
    endAt:
      props.subjects &&
      moment.max(props.subjects.map((subject) => subject.endAt)).format(),
  }

  // If we're editing a case, we'll need to refresh all the exposures.
  if (props.id)
    mutationRefetchQueries = [
      {
        query: EXPOSED_ASSET_LIST,
        variables,
      },
      {
        query: EXPOSED_EMPLOYEE_LIST,
        variables,
      },
      {
        query: EXPOSED_LOCATION_LIST,
        variables,
      },
      ...mutationRefetchQueries,
    ]

  const [
    putCase,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation(
    PUT_CASE,
    // The empty function supplied for the onError callback is designed to
    // resolve the error itself returned from the onSubmit Promise. This allows
    // us to pass errors out to components as props but to ensure that the
    // Promise can resolve successfully first.
    // For more information: https://stackoverflow.com/a/59472340
    {
      onCompleted: (data) =>
        props.onSuccess && props.onSuccess(data.putContactTracingCase),
      onError: () => {},
      refetchQueries: mutationRefetchQueries,
      awaitRefetchQueries: true,
    }
  )

  const { loading, error, data } = useQuery(
    props.caseType === "patientPositive" ? LOCATIONS_QUERY : USERS_QUERY,
    {
      variables: {
        facilityId: facilityId,
      },
    }
  )

  if (loading) return <Loading />

  if (error) throw error

  const newProps = {
    ...props,
    timezone: data.facility.timezone,
    autosuggestData: data.facility.listItems.map((item) => ({
      id: item.id,
      name: item.name,
      subName: item.subName.name,
    })),
    errors: mutationError && [mutationError.toString()],
    submitLoading: mutationLoading,
    onSubmit: (subjects) => {
      putCase({
        variables: {
          id: props.id,
          type: props.caseType,
          facilityId: facilityId,
          subjects: subjects.map((subject) => ({
            entityId: subject.entityId,
            startAt: subject.startAt.format(),
            endAt: subject.endAt.format(),
          })),
          archive: props.archive,
          delete: props.delete,
        },
      })
    },
  }

  return <CreateEditCase {...newProps} />
}

CreateEditCaseEnhanced.propTypes = {
  subjects: subjectsPropTypes,
  onSuccess: PropTypes.func.isRequired,
  caseType: PropTypes.oneOf(["patientPositive", "employeePositive"]).isRequired,
  id: PropTypes.string,
  internalId: PropTypes.string,
  archive: PropTypes.bool,
  delete: PropTypes.bool,
}

export default CreateEditCaseEnhanced
